import React from "react";
import { Link } from "react-router-dom";
import { PageHero, Eyebrow } from "../components/site/kit";
import { NextSteps } from "../components/site/extras";
import { Reveal } from "../components/site/motion";
import { useAbout } from "../lib/aboutApi";

const PersonCard = ({ p, i, testId }) => (
  <Reveal delay={(i % 3) * 0.06} data-testid={testId} className="flex flex-col border border-white/12 bg-[#1b1f19] p-6">
    {p.image ? (
      <img src={p.image} alt={p.name} className="mb-5 aspect-square w-full object-cover" />
    ) : (
      <div aria-hidden className="mb-5 flex aspect-square w-full items-center justify-center bg-white/5 font-display text-6xl text-[#5f8f10]">
        {(p.name || "?").charAt(0)}
      </div>
    )}
    <h3 className="font-display text-xl uppercase leading-tight tracking-tight text-white">{p.name}</h3>
    <p className="font-body mt-1 text-sm font-semibold uppercase tracking-[0.12em] text-[#5f8f10]">{p.role}</p>
    {(p.longBio || p.bio) && (
      <p className="font-body mt-4 text-base font-normal leading-relaxed text-white/70">{p.longBio || p.bio}</p>
    )}
  </Reveal>
);

const Group = ({ label, people, tier }) => {
  if (!people.length) return null;
  return (
    <div data-testid={`team-group-${tier}`}>
      <Eyebrow dark>{label}</Eyebrow>
      <div className="mt-10 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
        {people.map((p, i) => (
          <PersonCard key={i} p={p} i={i} testId={`team-card-${tier}-${i}`} />
        ))}
      </div>
    </div>
  );
};

export default function Team() {
  const about = useAbout();
  const published = (about.leaders || []).filter((l) => l.published !== false && (l.name || "").trim());
  const leadership = published.filter((l) => (l.tier || "leadership") === "leadership");
  const wider = published.filter((l) => (l.tier || "leadership") === "team");

  return (
    <div className="font-body bg-white">
      <PageHero
        testId="team-hero"
        eyebrow="Team"
        crumbs={[{ label: "Home", href: "/" }, { label: "About", href: "/about" }, { label: "Team" }]}
        title={<>The people who stay <span className="text-[#5f8f10]">in the room.</span></>}
        subcopy="Senior operators and strategists brought together around one agenda: diagnose the real constraint, build strategy around execution, and stay until the business moves."
      />

      <section data-testid="team-list" className="bg-[#161A15]">
        <div className="mx-auto max-w-[1360px] space-y-20 px-6 py-24 md:px-12 md:py-28 lg:px-16">
          <Group label="Leadership" people={leadership} tier="leadership" />
          <Group label="The wider team" people={wider} tier="team" />
          {published.length === 0 && (
            <p data-testid="team-empty" className="font-body text-lg text-white/60">Team members will appear here soon.</p>
          )}
        </div>
      </section>

      <NextSteps links={[
        { eyebrow: "About", title: "How we think", blurb: "Why we connect the disciplines that affect growth around one problem.", href: "/about" },
        { eyebrow: "Careers", title: "Work with us", blurb: "Problems that do not fit neatly into one discipline.", href: "/careers" },
        { eyebrow: "Contact", title: "Start the conversation", blurb: "Bring us the growth problem in front of you.", href: "/contact" },
      ]} />
    </div>
  );
}
